import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ToastaService} from 'ngx-toasta';
import {forkJoin, Observable, of, OperatorFunction, pipe} from 'rxjs';
import {catchError, filter, map, switchMap, take, tap} from 'rxjs/operators';
import {ConfigurationService} from '../../../../entry-routes/configuration/configuration.service';
import {EsPage} from '../../../models/es-page';
import { Field, FieldType } from '../../../models/field';
import {ListValueForAutocomplete} from '../../../models/list-value-for-autocomplete';
import {SearchPath} from '../../../models/search-path';
import {Url} from '../../../models/url';
import {Utils} from '../../../utils/utils';
import {GedFieldState} from '../reducer/ged/ged-cfg-field.reducer';
import {WkfFieldState} from '../reducer/wkf/wkf-cfg-field.reducer';
import { Privacy } from '../../../models/privacy';

@Injectable({
  providedIn: 'root'
})
export abstract class AbstractCfgFieldService {

  static readonly I18N_SUFFIX = 'i18n';
  static readonly CODE_SUFFIX = 'code';
  protected static readonly FIELD_PAGE_SIZE = 24;
  protected static readonly PATTERN_PARAM = '_pattern';
  protected static readonly EXCLUDE_PARAM = '_exclude';
  protected getFieldApiUrl = '';

  constructor(
    protected http: HttpClient,
    protected configService: ConfigurationService,
    protected toastaService: ToastaService
  ) {
    this.configService.applicationConfigReady$.subscribe(() => this.init())
  }

  // protected static pathToCode(path: string): string {
  //   const newPath = AbstractCfgFieldService.removeSuffixFromPath(path);
  //   return newPath.substring(newPath.lastIndexOf('.') + 1);
  // }

  protected static query(): Observable<HttpParams> {
    const httpParams = new HttpParams();
    return of(httpParams);
  }

  protected static removeSuffixFromPath(path: string): string {
    let newPath = path;
    const fieldCode = path.substring(path.indexOf('cdx_datas.') + 10);
    if (fieldCode.indexOf('.') > 0) {
      newPath = path.substring(0, path.lastIndexOf('.'));
    }
    return newPath;
  }

  /*START public methods*/
  public loadFields(...fieldCodes: string[]): Observable<{ [p: string]: Field }> {
    if ( fieldCodes.length===1 && fieldCodes[0]=='assignation') {
      console.debug('loadFields assignation ...');
      return of({ 'assignation': { code:'assignation' , type: FieldType.STRING, label: 'MOCK assignation', format:'MOCK monFormatBidon', privacy: Privacy.PUBLIC} });
    }


    let firstPass = true;
    fieldCodes = fieldCodes.filter((value, index, self) => self.indexOf(value) === index && value.length);
    if (!fieldCodes.length) {
      return of({});
    }
    // TODO to be removed after API is ready to send the error on every field...
    const cfgFieldStoreState: GedFieldState | WkfFieldState = this.getCfgFieldStoreState();
    if (!!cfgFieldStoreState && !!cfgFieldStoreState.error) {
      this.cfgFieldActionLoadStarted();
      console.log(`cfgFieldStoreState = `, cfgFieldStoreState.error)
      this.toastaService.error(JSON.stringify(cfgFieldStoreState.error));
      this.cfgFieldActionLoadSucceeded(cfgFieldStoreState.datas);
    }
    const observable = this.getCfgFieldStore().pipe(
      filter((state: GedFieldState | WkfFieldState) => undefined === state || (!state.loading && state.error == null)),
      tap((state: GedFieldState | WkfFieldState) => {
        if (!state) {
          if (firstPass) {
            firstPass = false;
            this._loadFields(...fieldCodes);
          }
          return;
        }
        const existingFields = state.datas;
        const unknowFields = [];
        for (let index = 0; index < fieldCodes.length; index++) {
          const fieldCode = fieldCodes[index];
          if (undefined === existingFields[fieldCode]) {
            unknowFields.push(fieldCode);
          }
        }
        if (firstPass && unknowFields.length > 0) {
          firstPass = false;
          this._loadFields(...unknowFields);
        }
      })
    );

    observable.subscribe();
    // l'observable retourné :
    return observable.pipe(
      filter((state: GedFieldState | WkfFieldState) => undefined !== state && !state.loading && state.error == null), // chargé, sans erreur
      map((state: GedFieldState | WkfFieldState) => state.datas),
      map(fieldMap => {  // contient uniquement les champs requêtés
        const result: { [key: string]: Field } = {};
        fieldCodes.forEach(fieldCode => {
          result[fieldCode] = fieldMap[fieldCode];
        });
        return result;
      }),
      filter(fieldMap => { // contient tous les champs requêtés
        const foundedFields: Field[] = Object.values(fieldMap);
        return foundedFields.length > 0 && !foundedFields.some(value => !value);
      }),
      take(1) // one shot
    ); // pie
  }

  /*END getting urls*/

  public loadFieldsFromPath(...paths: string[]): Observable<{ [p: string]: Field }> {
    if (paths.length === 0) {
      return of({});
    }
    const codePathMap: { [p: string]: string } = {};
    // paths.forEach(path => codePathMap[AbstractCfgFieldService.pathToCode(path)] = path);
    paths.forEach(path => codePathMap[new SearchPath(path).code] = path);
    return this.loadFields(
      ...Object.keys(codePathMap))
      .pipe(
        map((fieldMap: { [p: string]: Field }) => {
          const pathFieldMap: { [p: string]: Field } = {};
          Object.keys(fieldMap).forEach(code => pathFieldMap[codePathMap[code]] = fieldMap[code]);
          return pathFieldMap;
        })
      );
  }

  public removeFields(): void {
    this.cfgFieldActionRemoveAllStarted();
    this.cfgFieldActionRemoveAllSucceeded();
  }

  public searchFieldListValues(fieldCode: string, pattern = '', excludes: string[], size: number = AbstractCfgFieldService.FIELD_PAGE_SIZE, page = 0): Observable<EsPage<ListValueForAutocomplete>> {
    console.log('searchFieldListValues size: '+size+ ' page:'+page);
    return AbstractCfgFieldService.query()
      .pipe(
        // this.coordParams(size, page),
        this.setPattern(pattern),
        this.excludes(excludes),
        this.requestFieldList(fieldCode)
      );
  }

  public getFieldListValues(fieldCode: string, fieldListValues: string[]): Observable<ListValueForAutocomplete[]> {
    const requests$: Observable<ListValueForAutocomplete>[] = [];
    fieldListValues.forEach((value: string) => {
      if (Utils.notNullAndNotUndefined(value)) {
        const fieldListValue = this.getFieldListValue(fieldCode, value)
          .pipe(catchError(err => {
            console.error(err);
            return of({
              value: value,
              label: value,
              status: 'DELETED'
            } as ListValueForAutocomplete);
          }))
        ;
        requests$.push(fieldListValue);
      }
    });
    return forkJoin(requests$);
  }

  public getFieldListValue(fieldCode: string, fieldListValues: string): Observable<ListValueForAutocomplete> {
    try {
      return AbstractCfgFieldService.query().pipe(
        this.setListValueToParams(fieldListValues),
        this.requestFieldListValueObject(fieldCode)
      );
    } catch (e) {
      return of(e);
    }
  }

  /*END calling actions*/

  /*START getting urls*/
  protected abstract getApiBaseUrl(): string;

  /*START calling actions*/
  protected abstract cfgFieldActionLoadStarted(): void;

  /*END getting fields store*/

  protected abstract cfgFieldActionLoadSucceeded(keyValue: { [key: string]: Field }): void;

  /*END methods called from abstract && extenders*/

  /*START methods called from abstract*/

  protected abstract cfgFieldActionLoadFailed(error: any): void;

  protected abstract cfgFieldActionRemoveAllStarted(): void;

  protected abstract cfgFieldActionRemoveAllSucceeded(): void;

  /*START getting fields store*/
  protected abstract getCfgFieldStore(): Observable<GedFieldState | WkfFieldState>;

  /*END methods called from abstract*/

  protected abstract getCfgFieldStoreState(): GedFieldState | WkfFieldState;

  /*START methods called from abstract && extenders*/
  protected _getStoreFieldByCode(fieldsState: GedFieldState | WkfFieldState, fieldCode: string): Field {
    if (!!fieldsState && !!fieldsState.datas) {
      return fieldsState.datas[fieldCode];
    }
    return null;
  }

  private init(): void {
    this.getFieldApiUrl = this.getApiBaseUrl();
  }

  // TODO see if needs WKF cfgFieldsService also, if not, to be moved in gedFieldsService

  private _loadFields(...fieldCodes: string[]): Observable<{ [key: string]: Field }> {
    this.cfgFieldActionLoadStarted();
    try {
      const observable = this._requestFields(fieldCodes);
      observable
        .subscribe((keyValueList: { [key: string]: Field }) => {
          this.cfgFieldActionLoadSucceeded(keyValueList);
        }, (response: HttpErrorResponse) => {
          this.cfgFieldActionLoadFailed(response.error);
        });
      return observable;
    } catch (error) {
      this.cfgFieldActionLoadFailed(error);
    }
  }

  private _httpRequestFields(fieldCodes: string[]): Observable<NonNullable<unknown>> {
    const fieldJoin = fieldCodes.join();
    const httpParams = (new HttpParams()).set('code', fieldJoin);
    const objectObservable = this.http.get(this.getFieldApiUrl, {params: httpParams});
    return objectObservable;
  }

  private _requestFields(fieldCodes: string[]): Observable<{ [key: string]: Field }> {
    return this._httpRequestFields(fieldCodes)
      .pipe(
        map((body: any) => {
          const keyValueList: { [key: string]: Field } = {};
          Object.keys(body).forEach(code => {
            if (!!body[code]) {
              keyValueList[code] = Field.fromJson(body[code], Field);
            }
          });
          return keyValueList;
        })
      );
  }

  private setListValueToParams(fieldListValue: string): OperatorFunction<HttpParams, HttpParams> {
    return pipe(
      map((params: HttpParams) => {
        if (!!fieldListValue) {
          params = params.append('value', fieldListValue);
        }
        return params;
      })
    );
  }

  private requestFieldListValueObject(fieldCode: string): OperatorFunction<HttpParams, ListValueForAutocomplete> {
    return switchMap(params => {
      return this.http.get<ListValueForAutocomplete>(this.getFieldApiUrl + fieldCode + '/' + Url.LIST_ITEM, {params: params}).pipe(
        map( (itemListeOrigin) => Object.assign( new ListValueForAutocomplete(itemListeOrigin.value, itemListeOrigin.label), itemListeOrigin) )
      );
    });
  }

  private setPattern(pattern = ''): OperatorFunction<HttpParams, HttpParams> {
    return map((params: HttpParams) => params.append(AbstractCfgFieldService.PATTERN_PARAM, pattern));
  }

  private excludes(excludeValues: string[]): OperatorFunction<HttpParams, HttpParams> {
    return map((params: HttpParams) => {
      let excludeValueList = '';
      if (excludeValues.length) {
        excludeValues.forEach((excludedValue: string, index: number) => {
          excludeValueList = excludeValueList + excludedValue;
          if (index < excludeValues.length - 1) {
            excludeValueList = excludeValueList + ',';
          }
        });
      }
      return params.append(AbstractCfgFieldService.EXCLUDE_PARAM, excludeValueList);
    });
  }

  private requestFieldList(fieldCode: string): OperatorFunction<HttpParams, EsPage<ListValueForAutocomplete>> {
    return switchMap(params => {
      return this.http.get<EsPage<ListValueForAutocomplete>>(this.getFieldApiUrl + fieldCode + '/' + Url.LIST, {params});
    });
  }

  /*END public methods*/
}
